/**
 * Service Demo Page
 * =================
 * 
 * Interactive service booking demo for the homepage.
 * Uses mocked data - no real payments or bookings.
 * 3-step flow: Select Service -> Pick Time -> Success (mocked payment)
 */

import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  User,
  Check,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Wrench,
  MessageCircle,
  Settings,
  Truck,
  Building,
  PoundSterling,
  CreditCard,
  Home,
  Sparkles,
  CalendarDays,
} from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

// Demo services (mocked)
const DEMO_SERVICES = [
  {
    service_id: "demo_svc_1",
    name: "Phone Screen Repair",
    description: "Professional screen repair for all phone models. We use OEM-quality parts with a 12-month warranty.",
    category_id: "repair",
    duration_minutes: 60,
    base_price: 4999, // £49.99
    deposit_percent: 20,
  },
  {
    service_id: "demo_svc_2",
    name: "IT Consultation",
    description: "Expert advice on your tech setup. We'll help optimize your systems and solve any issues.",
    category_id: "consultation",
    duration_minutes: 45,
    base_price: 7500, // £75.00
    deposit_percent: 25,
  },
  {
    service_id: "demo_svc_3",
    name: "Computer Maintenance",
    description: "Full system check, cleaning, and optimization. Speed up your PC and extend its life.",
    category_id: "maintenance",
    duration_minutes: 90,
    base_price: 5999, // £59.99
    deposit_percent: 20,
  },
  {
    service_id: "demo_svc_4",
    name: "Equipment Delivery",
    description: "Fast and secure delivery of IT equipment to your location. Same-day available.",
    category_id: "delivery",
    duration_minutes: 30,
    base_price: 1999, // £19.99
    deposit_percent: 50,
  },
];

// Demo staff
const DEMO_STAFF = [
  { staff_id: "demo_staff_1", name: "Sarah Johnson" },
  { staff_id: "demo_staff_2", name: "Michael Chen" },
];

const CATEGORY_ICONS = {
  repair: Wrench,
  consultation: MessageCircle,
  maintenance: Settings,
  delivery: Truck,
};

const CATEGORY_COLORS = {
  repair: "border-red-500/30 hover:border-red-500/50",
  consultation: "border-blue-500/30 hover:border-blue-500/50",
  maintenance: "border-green-500/30 hover:border-green-500/50",
  delivery: "border-amber-500/30 hover:border-amber-500/50",
};

const ServiceDemoPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Check if user is a merchant with service management
  const isServiceMerchant = user?.role === "merchant" && user?.is_approved;
  
  // Demo flow state - 3 steps
  const [step, setStep] = useState(1); // 1: Select Service, 2: Select Time, 3: Success
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Generate available dates (next 7 days)
  const getAvailableDates = useCallback(() => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        date: date.toISOString().split("T")[0],
        dayName: date.toLocaleDateString("en-GB", { weekday: "short" }),
        dayNum: date.getDate(),
        month: date.toLocaleDateString("en-GB", { month: "short" }),
      });
    }
    
    return dates;
  }, []);

  const availableDates = getAvailableDates();

  // Generate available slots
  const getAvailableSlots = () => {
    const slots = [];
    for (let hour = 9; hour < 17; hour++) {
      slots.push(`${hour.toString().padStart(2, "0")}:00`);
      slots.push(`${hour.toString().padStart(2, "0")}:30`);
    }
    return slots;
  };

  const availableSlots = getAvailableSlots();

  // Handle service selection
  const selectService = (service) => {
    setSelectedService(service);
    setSelectedStaff(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setStep(2);
  };

  // Handle time selection
  const selectTime = (staffId, time) => {
    setSelectedStaff(staffId);
    setSelectedTime(time);
  };

  // Handle booking submission (mocked)
  const handleBook = () => {
    setProcessing(true);
    // Simulate processing
    setTimeout(() => {
      setProcessing(false);
      setStep(3);
      toast.success("Demo booking confirmed!");
    }, 1500);
  };

  // Reset demo
  const resetDemo = () => {
    setStep(1);
    setSelectedService(null);
    setSelectedStaff(null);
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-dark border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3">
              <img src={COMPANY_LOGO} alt="Veyronix" className="h-8" />
            </Link>
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
              DEMO MODE
            </Badge>
          </div>
          
          <div className="flex items-center gap-3">
            {step > 1 && step < 3 && (
              <Button
                variant="ghost"
                onClick={() => setStep(step - 1)}
                className="text-gray-500"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            <Link to="/">
              <Button variant="ghost" className="text-gray-500">
                <Home className="h-4 w-4 mr-2" />
                Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Demo Banner */}
      <div className="bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border-b border-amber-500/20">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-amber-400" />
          <p className="text-sm text-amber-300">
            This is a demo - no real bookings or payments will be made.
            {isServiceMerchant && (
              <Link to="/merchant/services" className="ml-2 underline text-amber-400 hover:text-amber-300">
                Go to my Service Management →
              </Link>
            )}
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  s < step
                    ? "bg-emerald-500 text-gray-900"
                    : s === step
                    ? "bg-indigo-600 text-gray-900"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {s < step ? <Check className="h-4 w-4" /> : s}
              </div>
              {s < 3 && (
                <div
                  className={`w-16 h-0.5 mx-1 ${
                    s < step ? "bg-emerald-500" : "bg-gray-100"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Select Service */}
        {step === 1 && (
          <div>
            <h2 className="text-2xl font-bold text-center mb-2">Select a Service</h2>
            <p className="text-gray-500 text-center mb-8">Experience our service booking flow</p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {DEMO_SERVICES.map((service) => {
                const Icon = CATEGORY_ICONS[service.category_id] || Settings;
                const colorClass = CATEGORY_COLORS[service.category_id] || "border-slate-500/30";
                
                return (
                  <button
                    key={service.service_id}
                    onClick={() => selectService(service)}
                    className={`text-left p-5 rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 border-2 transition-all hover:bg-white ${colorClass}`}
                    data-testid={`demo-service-${service.service_id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-2 rounded-lg bg-white/5">
                        <Icon className="h-6 w-6 text-indigo-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">{service.name}</h3>
                        <p className="text-sm text-gray-500 line-clamp-2 mb-3">{service.description}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-gray-500">
                            <Clock className="h-4 w-4" />
                            {service.duration_minutes}min
                          </span>
                          <span className="flex items-center gap-1 text-indigo-400 font-semibold">
                            <PoundSterling className="h-4 w-4" />
                            {formatPrice(service.base_price)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Select Date & Time */}
        {step === 2 && selectedService && (
          <div>
            <h2 className="text-2xl font-bold text-center mb-2">Select Date & Time</h2>
            <p className="text-gray-500 text-center mb-8">
              {selectedService.name} - {selectedService.duration_minutes}min
            </p>
            
            {/* Date Picker */}
            <div className="mb-8">
              <h3 className="text-sm text-gray-500 mb-3">Choose a date</h3>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {availableDates.map((d) => (
                  <button
                    key={d.date}
                    onClick={() => setSelectedDate(d.date)}
                    className={`flex-shrink-0 w-16 py-3 rounded-xl border text-center transition-all ${
                      selectedDate === d.date
                        ? "bg-indigo-600 border-indigo-500 text-gray-900"
                        : "bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 border-gray-200 hover:border-indigo-500/50"
                    }`}
                  >
                    <p className="text-xs text-gray-500">{d.dayName}</p>
                    <p className="text-lg font-bold">{d.dayNum}</p>
                    <p className="text-xs">{d.month}</p>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Time Slots */}
            {selectedDate && (
              <div className="mb-8">
                <h3 className="text-sm text-gray-500 mb-3">Available times</h3>
                
                <div className="space-y-4">
                  {DEMO_STAFF.map((staff) => (
                    <div key={staff.staff_id} className="p-4 rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 border border-gray-200">
                      <div className="flex items-center gap-2 mb-3">
                        <User className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">{staff.name}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {availableSlots.slice(0, 8).map((slot) => (
                          <button
                            key={`${staff.staff_id}-${slot}`}
                            onClick={() => selectTime(staff.staff_id, slot)}
                            className={`px-4 py-2 rounded-lg border text-sm transition-all ${
                              selectedStaff === staff.staff_id && selectedTime === slot
                                ? "bg-indigo-600 border-indigo-500"
                                : "bg-gray-100 border-gray-200 hover:border-indigo-500/50"
                            }`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Continue Button */}
            {selectedDate && selectedTime && (
              <div className="max-w-md mx-auto">
                {/* Summary */}
                <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 border border-gray-200 mb-6">
                  <h3 className="font-semibold mb-3">{selectedService.name}</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {new Date(selectedDate).toLocaleDateString("en-GB", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                      })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {selectedTime}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <User className="h-4 w-4" />
                    <span>{DEMO_STAFF.find(s => s.staff_id === selectedStaff)?.name}</span>
                  </div>
                </div>

                {/* Deposit Info */}
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <PoundSterling className="h-4 w-4 text-amber-400" />
                    <span className="font-medium text-amber-400">Deposit Required</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    {selectedService.deposit_percent}% deposit ({formatPrice(selectedService.base_price * selectedService.deposit_percent / 100)})
                    would be taken at booking.
                  </p>
                </div>
                
                <Button
                  onClick={handleBook}
                  disabled={processing}
                  className="w-full h-12 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="demo-confirm-booking"
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing Demo...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Complete Demo Booking
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-emerald-500" />
            </div>
            
            <h2 className="text-2xl font-bold mb-2">Demo Booking Complete!</h2>
            <p className="text-gray-500 mb-6">
              In the real system, you would receive a confirmation email and the booking would appear in your merchant dashboard.
            </p>
            
            <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 border border-gray-200 mb-6 text-left">
              <h3 className="font-semibold mb-3">{selectedService?.name}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-500">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {selectedDate && new Date(selectedDate).toLocaleDateString("en-GB", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Clock className="h-4 w-4" />
                  <span>{selectedTime}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <User className="h-4 w-4" />
                  <span>{DEMO_STAFF.find(s => s.staff_id === selectedStaff)?.name}</span>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Deposit (demo)</span>
                  <span className="text-emerald-400">
                    {formatPrice(selectedService?.base_price * selectedService?.deposit_percent / 100)}
                  </span>
                </div>
                <div className="flex justify-between font-medium mt-1">
                  <span>Total</span>
                  <span>{formatPrice(selectedService?.base_price)}</span>
                </div>
              </div>
            </div>
            
            {/* CTA for real service */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 mb-6">
              <h4 className="font-semibold text-indigo-400 mb-2">Ready to get started?</h4>
              <p className="text-sm text-gray-500 mb-4">
                Sign up as a merchant to manage your own services and bookings.
              </p>
              <Link to="/register">
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                  Start Free Trial
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={resetDemo} className="border-gray-200">
                Try Again
              </Button>
              <Link to="/">
                <Button variant="ghost" className="text-gray-500">
                  <Home className="h-4 w-4 mr-2" />
                  Home
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceDemoPage;
